import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { IRoute, useAuth, useRouter } from 'AurionCR/components';
import { AppScrollbars } from 'components/app-scrollbar';
import {
  createRef,
  forwardRef,
  memo,
  Ref,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useAppSelector } from 'store';

export type OnMenuOpen = (data: { isAccordionOpen: boolean; ref: any; route: IRoute }) => void;

interface MainNavProps {
  collapsed?: boolean;
  accordion?: boolean;
  onMenuOpen?: OnMenuOpen;
}

export const checkActive = (pathname: string, path: string): boolean => {
  // eslint-disable-next-line
  return path === pathname || (path.length > 1 && new RegExp(`${path}(\/|$)`, 'g').test(pathname));
};
export const MenuItem = memo(
  forwardRef(
    ({ route, pathname, collapsed, level, goTo, onOpen, onMenuOpen }: any, ref: Ref<any>) => {
      const auth = useAuth();
      const children = useMemo(() => {
        if (route.routes) {
          return route.routes.filter(
            (route: IRoute) => route.inMenu && auth.permission(route.permission),
          );
        } else {
          return [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [route]);

      const [active, setActive] = useState(checkActive(pathname, route.path));
      const [open, setOpen] = useState(checkActive(pathname, route.path));
      const onClick = useCallback(() => {
        if (route.component) {
          if (onOpen) onOpen(true, ref, route);
          goTo(route.path);
        } else {
          const open_ = !open;
          if (onOpen) onOpen(open_, ref, route);
          setOpen(open_);
        }
        onMenuOpen && onMenuOpen({ isAccordionOpen: open, ref, route });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [route, setOpen, onOpen, open, onMenuOpen]);
      const iconCollapsed = useMemo(() => {
        if (children.length && level === 1) {
          return !collapsed && open ? (
            <ExpandLess className="icon-collapsed" />
          ) : (
            <ExpandMore className="icon-collapsed" />
          );
        } else {
          return null;
        }
      }, [children, open, collapsed, level]);
      const iconRoute = useMemo(() => {
        if (route.icon) {
          if (typeof route.icon === 'string') {
            return (
              <ListItemIcon className="icon-wrapper">
                <span className={`route-icon ${route.icon}`} />
              </ListItemIcon>
            );
          } else {
            return <ListItemIcon className="icon-wrapper">{route.icon.type.render()}</ListItemIcon>;
          }
        } else {
          return null;
        }
      }, [route]);
      useEffect(() => {
        setActive(checkActive(pathname, route.path));
      }, [route.path, setActive, pathname]);
      // eslint-disable-next-line
      useImperativeHandle(ref, () => ({ setOpen }));
      return !auth.permission(route.permission) ||
        (route.routes && !children.length) ||
        level === 3 ? null : (
        <>
          <ListItemButton onClick={onClick} className={`level-${level} ${active ? 'active' : ''}`}>
            {iconRoute}
            <ListItemText
              className="title"
              color="primary"
              primary={route.title}
              secondary={children.map((item: IRoute) => item.title).join(', ')}
            />
            {iconCollapsed}
          </ListItemButton>
          {children.length > 0 && (
            <Collapse in={!collapsed && open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {children.map((item: IRoute) => (
                  <MenuItem
                    key={item.path}
                    route={item}
                    pathname={pathname}
                    collapsed={collapsed}
                    onMenuOpen={onMenuOpen}
                    level={level + 1}
                    goTo={goTo}
                  />
                ))}
              </List>
            </Collapse>
          )}
        </>
      );
    },
  ),
);

export const MainNav = memo<MainNavProps>(
  ({ collapsed = false, accordion = false, onMenuOpen = () => void 0 }) => {
    const routes = useAppSelector((state) => state.routes.routes);
    const router = useRouter();
    const goTo = useCallback((path: string) => {
      router.push(path);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [elRefs, setElRefs] = useState<RefObject<IRoute>[]>([]);
    const [menu, setMenu] = useState<IRoute[]>([]);
    const onOpen = useCallback(
      (state: boolean, ref: any) => {
        if (state && accordion) {
          elRefs.forEach((item: any) => {
            if (ref !== item) item?.current?.setOpen(false);
          });
        }
        // some
      },
      [accordion, elRefs],
    );
    // init
    useEffect(() => {
      if (routes && routes.length) {
        const menu_ = routes.filter((route: IRoute) => route.inMenu);
        setElRefs(menu_.map(() => createRef()));
        setMenu(menu_);
      }
    }, [routes]);

    if (!menu.length) return null;
    return (
      <AppScrollbars
        className={'r-scroll-bar'}
        mode={collapsed ? 'hide' : 'hover'}
        style={{
          overflowX: 'hidden',
        }}
      >
        <nav className={`main-nav ${collapsed ? 'collapsed' : ''}`}>
          <List component="div" disablePadding>
            {menu.map((route: IRoute, i: number) => (
              <MenuItem
                key={route.path}
                ref={elRefs[i]}
                route={route}
                pathname={router.pathname}
                collapsed={collapsed}
                onOpen={onOpen}
                onMenuOpen={onMenuOpen}
                level={1}
                goTo={goTo}
              />
            ))}
          </List>
        </nav>
      </AppScrollbars>
    );
  },
);

export default MainNav;
